import { useState, useEffect, useCallback } from "react";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { getResolvedClaimsValues } from "@/resources/claimants/hooks/useCurrentPolicyData";
import { claimantHasStoplossClaims } from "@/resources/claimants/helpers/familyEligibility";
import type { FamilyAggregateResponse, FamilyMember, FamilyMemberRaw } from "@/types";

export interface FamilyUnitData {
  subscriber: FamilyAggregateResponse["subscriber"];
  members: FamilyMember[];
  familyTotalSpend: number;
  familyDeductible: number | null;
  individualLimit: number | null;
  maxMembersCounted: number | null;
  isBreach: boolean;
  calculationHeld: boolean;
}

interface UseFamilyUnitResult {
  data: FamilyUnitData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Build a display name from the claimant's name fields. */
function buildMemberName(raw: FamilyMemberRaw): string {
  if (raw.claimant_first_name && raw.claimant_last_name) {
    return `${raw.claimant_first_name} ${raw.claimant_last_name}`;
  }
  return raw.claimant_name || raw.aegle_id || "Unknown";
}

/**
 * The member's authoritative total paid on the family unit's policy, the same scope
 * `family_total_spend` uses, so the rows reconcile with the bar. Routed through the
 * shared resolver so a carve-out member reads the server blend and a
 * reported-authority member the carrier figure (ADR-051 §7 / ADR-077 §7); this hook
 * used to re-derive authority itself. Without a subscriber policy it falls back to
 * summing every term.
 */
function extractTotalSpend(raw: FamilyMemberRaw, familyPolicyId?: string): number {
  const policyData = raw.policy_data ?? {};
  const scoped = familyPolicyId ? [policyData[familyPolicyId]] : Object.values(policyData);
  let total = 0;
  for (const pd of scoped) {
    total += Number(getResolvedClaimsValues(pd)?.total_paid) || 0;
  }
  return total;
}

/** Normalize an API member to the display-friendly shape. */
function normalizeMember(raw: FamilyMemberRaw, familyPolicyId?: string): FamilyMember {
  return {
    id: raw.id,
    aegleId: raw.aegle_id,
    name: buildMemberName(raw),
    relationship: raw.claimant_relationship ?? "other",
    isPendingLink: raw.is_pending_link ?? false,
    memberId: raw.member_id,
    totalSpend: extractTotalSpend(raw, familyPolicyId),
    hasStoplossClaims: claimantHasStoplossClaims(raw),
  };
}

/**
 * Fetches the family unit aggregate for a given subscriber.
 * Calls GET /claimants/subscribers/{subscriberRefId}/family.
 * Only fetches when subscriberRefId is provided.
 */
export const useFamilyUnit = (subscriberRefId?: string): UseFamilyUnitResult => {
  // Keyed by the subscriber it was fetched for, so a change of subscriber never shows
  // the previous family while the new one loads, or after its fetch fails.
  const [loaded, setLoaded] = useState<{ forId: string; data: FamilyUnitData } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!subscriberRefId) {
      setLoaded(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const fetchFamily = async () => {
      try {
        // Uses httpClient directly (the same function the data provider uses internally)
        // because the family aggregate endpoint is a sub-resource that doesn't conform
        // to react-admin's getOne record interface (no top-level `id` field).
        const url = `${getPartnerAwareApiUrl()}/claimants/subscribers/${subscriberRefId}/family`;
        const { json: response } = await httpClient(url);

        if (cancelled) return;

        const familyPolicyId = response.subscriber?.policy_id_ref;
        const members = (response.family_members ?? []).map((m: FamilyMemberRaw) => normalizeMember(m, familyPolicyId));
        const familyDeductible = response.family_specific_deductible
          ? Number(response.family_specific_deductible)
          : null;
        const individualLimit = response.individual_limit ? Number(response.individual_limit) : null;
        const maxMembersCounted = response.max_members_counted ?? null;

        setLoaded({
          forId: subscriberRefId,
          data: {
            subscriber: response.subscriber,
            members,
            familyTotalSpend: Number(response.family_total_spend) || 0,
            familyDeductible,
            individualLimit,
            maxMembersCounted,
            isBreach: response.is_breach,
            calculationHeld: response.calculation_held,
          },
        });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error("Failed to load family unit"));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void fetchFamily();

    return () => {
      cancelled = true;
    };
  }, [subscriberRefId, refreshKey]);

  const data = loaded && loaded.forId === subscriberRefId ? loaded.data : null;
  return { data, isLoading, error, refetch };
};
